import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';

import { useIsUserAuthenticated } from '@/hooks/useIsUserAuthenticated';

import { AuthStrategyContext } from './AuthStrategyContext';
import type { AuthStrategyContextValue, EmbedStrategy } from './types';
import { useEmbedMessages } from './useEmbedMessages';

type EmbedStrategyProviderProps = {
  strategy: EmbedStrategy;
  children: ReactNode;
};

export const EmbedStrategyProvider = ({ children, strategy }: EmbedStrategyProviderProps) => {
  const isUserAuthenticated = useIsUserAuthenticated();
  const resolveRef = useRef<(() => void) | null>(null);

  const handleAuthComplete = useCallback(() => {
    resolveRef.current?.();
    resolveRef.current = null;
  }, []);

  const handleAuthCancel = useCallback(() => {
    resolveRef.current = null;
  }, []);

  const { requestAuth } = useEmbedMessages({
    strategy,
    onAuthComplete: handleAuthComplete,
    onAuthCancel: handleAuthCancel,
  });

  const handleAuth = useCallback(() => {
    if (isUserAuthenticated) return Promise.resolve();
    return new Promise<void>((resolve) => {
      resolveRef.current = resolve;
      requestAuth();
    });
  }, [isUserAuthenticated, requestAuth]);

  const contextValue: AuthStrategyContextValue = { strategy, handleAuth };

  return <AuthStrategyContext.Provider value={contextValue}>{children}</AuthStrategyContext.Provider>;
};
